import React, { useState } from 'react';
import { 
  CheckCircle2, 
  Clock, 
  Send, 
  XCircle, 
  ThumbsUp, 
  ThumbsDown, 
  ShoppingBag, 
  PackageCheck, 
  HelpCircle, 
  Truck, 
  TrendingUp,
  Inbox,
  Sparkles
} from 'lucide-react';

interface StatusData {
  pending_review: number;
  approved: number;
  sent: number;
  rejected: number;
  total: number;
}

interface FeedbackData {
  total_feedback: number;
  upvotes: number;
  downvotes: number;
  upvote_percentage: number;
}

interface CategoryItem {
  id: string;
  name: string;
  count: number;
  color: string;
  icon: React.ReactNode;
}

export const StatusDistributionChart: React.FC<{ data: StatusData; onSelectStatus?: (status: string) => void }> = ({ 
  data, 
  onSelectStatus 
}) => {
  const [hoveredSlice, setHoveredSlice] = useState<string | null>(null);

  const statuses = [
    { key: 'pending_review', label: 'Pending Review', value: data.pending_review || 0, color: '#f59e0b', hoverColor: '#d97706', icon: <Clock className="w-4 h-4 text-amber-500" /> },
    { key: 'approved', label: 'Approved', value: data.approved || 0, color: '#3b82f6', hoverColor: '#2563eb', icon: <CheckCircle2 className="w-4 h-4 text-blue-500" /> },
    { key: 'sent', label: 'Sent', value: data.sent || 0, color: '#10b981', hoverColor: '#059669', icon: <Send className="w-4 h-4 text-emerald-500" /> },
    { key: 'rejected', label: 'Rejected', value: data.rejected || 0, color: '#ef4444', hoverColor: '#dc2626', icon: <XCircle className="w-4 h-4 text-red-500" /> },
  ];

  const total = Math.max(1, data.total || statuses.reduce((acc, curr) => acc + curr.value, 0));

  let cumulativeAngle = 0;
  const radius = 65;
  const strokeWidth = 22;
  const center = 90;
  const circumference = 2 * Math.PI * radius;

  const slices = statuses.map((status) => {
    const percentage = total > 0 ? (status.value / total) * 100 : 0;
    const strokeDasharray = `${(percentage / 100) * circumference} ${circumference}`;
    const strokeDashoffset = -((cumulativeAngle / 360) * circumference);
    cumulativeAngle += (percentage / 100) * 360;

    return {
      ...status,
      percentage: Math.round(percentage),
      strokeDasharray,
      strokeDashoffset,
    };
  });

  return (
    <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            Status Breakdown
          </h3>
          <p className="text-xs text-gray-500">Distribution of emails across review workflow</p>
        </div>
        <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-slate-100 text-slate-700">
          {total} Total
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
        <div className="md:col-span-5 flex justify-center items-center relative py-2">
          <svg width="180" height="180" viewBox="0 0 180 180" className="transform -rotate-90">
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="transparent"
              stroke="#f3f4f6"
              strokeWidth={strokeWidth}
            />
            {slices.map((slice) => (
              <circle
                key={slice.key}
                cx={center}
                cy={center}
                r={radius}
                fill="transparent"
                stroke={hoveredSlice === slice.key ? slice.hoverColor : slice.color}
                strokeWidth={hoveredSlice === slice.key ? strokeWidth + 3 : strokeWidth}
                strokeDasharray={slice.strokeDasharray}
                strokeDashoffset={slice.strokeDashoffset}
                className="transition-all duration-300 cursor-pointer"
                onMouseEnter={() => setHoveredSlice(slice.key)}
                onMouseLeave={() => setHoveredSlice(null)}
                onClick={() => onSelectStatus && onSelectStatus(slice.key)}
              />
            ))}
          </svg>

          <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none">
            <span className="text-2xl font-bold text-gray-900">
              {hoveredSlice 
                ? slices.find(s => s.key === hoveredSlice)?.value 
                : data.pending_review || 0}
            </span>
            <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wider">
              {hoveredSlice 
                ? slices.find(s => s.key === hoveredSlice)?.label 
                : 'Pending'}
            </span>
          </div>
        </div>

        <div className="md:col-span-7 space-y-2.5">
          {slices.map((item) => (
            <div
              key={item.key}
              onClick={() => onSelectStatus && onSelectStatus(item.key)}
              onMouseEnter={() => setHoveredSlice(item.key)}
              onMouseLeave={() => setHoveredSlice(null)}
              className={`flex items-center justify-between p-2.5 rounded-lg border transition-all cursor-pointer ${
                hoveredSlice === item.key 
                  ? 'bg-slate-50 border-slate-300 shadow-sm' 
                  : 'bg-gray-50/50 border-gray-100 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-2.5">
                <div 
                  className="w-3 h-3 rounded-full flex-shrink-0" 
                  style={{ backgroundColor: item.color }} 
                />
                <div className="flex items-center gap-1.5">
                  {item.icon}
                  <span className="text-xs font-medium text-gray-700">{item.label}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-gray-900">{item.value}</span>
                <span className="text-xs font-medium text-gray-400 min-w-[32px] text-right">
                  {item.percentage}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const QualityScoreGauge: React.FC<{ data?: FeedbackData }> = ({ data }) => {
  const percentage = data?.upvote_percentage ?? 0;
  const upvotes = data?.upvotes ?? 0;
  const downvotes = data?.downvotes ?? 0;
  const total = data?.total_feedback ?? 0;

  let ratingLabel = 'No Ratings';
  let badgeColor = 'bg-gray-100 text-gray-600 border-gray-200';
  let strokeColor = '#9ca3af';

  if (total > 0) {
    if (percentage >= 90) {
      ratingLabel = 'Excellent AI Quality';
      badgeColor = 'bg-emerald-50 text-emerald-700 border-emerald-200';
      strokeColor = '#10b981';
    } else if (percentage >= 75) {
      ratingLabel = 'Good Quality';
      badgeColor = 'bg-blue-50 text-blue-700 border-blue-200';
      strokeColor = '#3b82f6';
    } else if (percentage >= 50) {
      ratingLabel = 'Moderate';
      badgeColor = 'bg-amber-50 text-amber-700 border-amber-200';
      strokeColor = '#f59e0b';
    } else {
      ratingLabel = 'Needs Improvement';
      badgeColor = 'bg-red-50 text-red-700 border-red-200';
      strokeColor = '#ef4444';
    }
  }

  const radius = 60;
  const strokeWidth = 14;
  const circumference = Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all flex flex-col justify-between">
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-purple-50 text-purple-600 rounded-lg">
              <Sparkles className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">AI Quality Analytics</h3>
              <p className="text-xs text-gray-500">Human review upvote accuracy</p>
            </div>
          </div>
          <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full border ${badgeColor}`}>
            {ratingLabel}
          </span>
        </div>

        <div className="flex flex-col items-center justify-center my-2 relative">
          <svg width="160" height="95" viewBox="0 0 160 95" className="overflow-visible">
            <path
              d="M 20 80 A 60 60 0 0 1 140 80"
              fill="none"
              stroke="#f3f4f6"
              strokeWidth={strokeWidth}
              strokeLinecap="round"
            />
            <path
              d="M 20 80 A 60 60 0 0 1 140 80"
              fill="none"
              stroke={strokeColor}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              className="transition-all duration-700 ease-out"
            />
          </svg>

          <div className="absolute top-10 flex flex-col items-center text-center">
            <span className="text-3xl font-extrabold text-gray-900 tracking-tight">
              {percentage}%
            </span>
            <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wider">
              Approval Rate
            </span>
          </div>
        </div>
      </div>

      <div className="pt-3 border-t border-gray-100 space-y-2">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-1.5 text-emerald-600 font-semibold">
            <ThumbsUp className="w-3.5 h-3.5" />
            <span>{upvotes} Upvotes</span>
          </div>
          <span className="text-gray-400 font-medium">{total} Total Rated</span>
          <div className="flex items-center gap-1.5 text-red-500 font-semibold">
            <ThumbsDown className="w-3.5 h-3.5" />
            <span>{downvotes} Downvotes</span>
          </div>
        </div>

        <div className="w-full bg-gray-100 h-2.5 rounded-full overflow-hidden flex">
          <div 
            className="bg-emerald-500 h-full transition-all duration-500" 
            style={{ width: `${total > 0 ? (upvotes / total) * 100 : 0}%` }}
          />
          <div 
            className="bg-red-500 h-full transition-all duration-500" 
            style={{ width: `${total > 0 ? (downvotes / total) * 100 : 0}%` }}
          />
        </div>
      </div>
    </div>
  );
};

const CATEGORY_CONFIG: Record<string, { name: string; color: string; iconKey: string }> = {
  'customer_question': { name: 'Customer Inquiries', color: '#3b82f6', iconKey: 'package' },
  'shipping_question': { name: 'Shipping & Delivery', color: '#f59e0b', iconKey: 'truck' },
  'damaged_defective': { name: 'Damaged / Defective', color: '#ef4444', iconKey: 'shopping' },
  'return_policy': { name: 'Returns & Refunds', color: '#f97316', iconKey: 'shopping' },
  'order_cancellation': { name: 'Order Cancellations', color: '#dc2626', iconKey: 'xcircle' },
  'product_question': { name: 'Product Questions', color: '#8b5cf6', iconKey: 'help' },
  'pending_response': { name: 'Pending Response', color: '#06b6d4', iconKey: 'clock' },
  'wrong_item': { name: 'Wrong Item Received', color: '#e11d48', iconKey: 'xcircle' },
  'battery_support': { name: 'Battery Support', color: '#10b981', iconKey: 'help' },
  'non_amazon': { name: 'Non-Amazon', color: '#6b7280', iconKey: 'inbox' },
};

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  'package': <PackageCheck className="w-4 h-4" />,
  'help': <HelpCircle className="w-4 h-4" />,
  'truck': <Truck className="w-4 h-4" />,
  'shopping': <ShoppingBag className="w-4 h-4" />,
  'xcircle': <XCircle className="w-4 h-4" />,
  'inbox': <Inbox className="w-4 h-4" />,
};

interface CategoryStatItem {
  category: string;
  count: number;
  percentage: number;
}

interface CategoryStatsData {
  categories: CategoryStatItem[];
  total: number;
}

export const CategoryBreakdownChart: React.FC<{ categoryData?: CategoryStatsData | null }> = ({ categoryData }) => {
  const categories: CategoryItem[] = (categoryData?.categories || []).map((cat) => {
    const config = CATEGORY_CONFIG[cat.category] || {
      name: cat.category.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      color: '#6b7280',
      iconKey: 'inbox',
    };
    return {
      id: cat.category,
      name: config.name,
      count: cat.count,
      color: config.color,
      icon: (
        <span style={{ color: config.color }}>
          {CATEGORY_ICONS[config.iconKey] || CATEGORY_ICONS['inbox']}
        </span>
      ),
    };
  });

  const totalEmails = categoryData?.total || 0;
  const maxCount = Math.max(1, ...categories.map(c => c.count));

  return (
    <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Email Category Insights</h3>
          <p className="text-xs text-gray-500">AI automated classification distribution</p>
        </div>
        <div className="p-1.5 bg-blue-50 text-blue-600 rounded-lg">
          <TrendingUp className="w-4 h-4" />
        </div>
      </div>

      {categories.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-gray-400">
          <Inbox className="w-8 h-8 mb-2" />
          <p className="text-sm font-medium">No categorized emails yet</p>
          <p className="text-xs">Process emails to see category distribution</p>
        </div>
      ) : (
        <div className="space-y-3.5">
          {categories.map((cat) => {
            const percentage = totalEmails > 0 ? Math.round((cat.count / totalEmails) * 100) : 0;
            const barWidth = Math.max(8, (cat.count / maxCount) * 100);

            return (
              <div key={cat.id} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2 font-medium text-gray-700">
                    {cat.icon}
                    <span>{cat.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-gray-900">{cat.count}</span>
                    <span className="text-gray-400 font-medium min-w-[32px] text-right">{percentage}%</span>
                  </div>
                </div>

                <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full transition-all duration-500" 
                    style={{ 
                      width: `${barWidth}%`, 
                      backgroundColor: cat.color 
                    }} 
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const PlatformShareChart: React.FC<{ 
  amazonEnabled: boolean; 
  ebayEnabled: boolean;
  onToggleAmazon: () => void;
  onToggleEbay: () => void;
  onToggleAll: () => void;
  isMonitorRunning: boolean;
}> = ({ amazonEnabled, ebayEnabled, onToggleAmazon, onToggleEbay, onToggleAll, isMonitorRunning }) => {
  return (
    <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all flex flex-col justify-between">
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Platform Integrations</h3>
            <p className="text-xs text-gray-500">Live background channels & monitoring controls</p>
          </div>
          <button
            onClick={onToggleAll}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all border ${
              isMonitorRunning
                ? 'bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100'
                : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
            }`}
          >
            {isMonitorRunning ? '● Monitoring Active' : 'Start Auto-Monitor'}
          </button>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between p-3.5 bg-gradient-to-r from-orange-50/50 to-amber-50/30 border border-orange-200/60 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-orange-500 text-white font-bold text-xs flex items-center justify-center shadow-sm">
                AMZ
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-semibold text-gray-900">Amazon Marketplace</h4>
                  <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-orange-100 text-orange-800 border border-orange-200">
                    DE / UK / US
                  </span>
                </div>
                <p className="text-xs text-gray-500">Auto IMAP order message parser</p>
              </div>
            </div>
            <button
              onClick={onToggleAmazon}
              className={`px-3 py-1 rounded-lg text-xs font-bold transition-all border ${
                amazonEnabled 
                  ? 'bg-emerald-500 text-white border-emerald-600 shadow-sm hover:bg-emerald-600' 
                  : 'bg-gray-200 text-gray-600 border-gray-300 hover:bg-gray-300'
              }`}
            >
              {amazonEnabled ? 'ENABLED' : 'DISABLED'}
            </button>
          </div>

          <div className="flex items-center justify-between p-3.5 bg-gradient-to-r from-blue-50/50 to-indigo-50/30 border border-blue-200/60 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-blue-600 text-white font-bold text-xs flex items-center justify-center shadow-sm">
                EBAY
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-semibold text-gray-900">eBay Store</h4>
                  <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-blue-100 text-blue-800 border border-blue-200">
                    GLOBAL
                  </span>
                </div>
                <p className="text-xs text-gray-500">Customer messages & inquiries</p>
              </div>
            </div>
            <button
              onClick={onToggleEbay}
              className={`px-3 py-1 rounded-lg text-xs font-bold transition-all border ${
                ebayEnabled 
                  ? 'bg-emerald-500 text-white border-emerald-600 shadow-sm hover:bg-emerald-600' 
                  : 'bg-gray-200 text-gray-600 border-gray-300 hover:bg-gray-300'
              }`}
            >
              {ebayEnabled ? 'ENABLED' : 'DISABLED'}
            </button>
          </div>
        </div>
      </div>

      <div className="pt-3 mt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${isMonitorRunning ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'}`} />
          <span>Polling frequency: <strong className="text-gray-700">Every 60s</strong></span>
        </div>
        <span className="text-gray-400 font-medium">Real-time sync</span>
      </div>
    </div>
  );
};

export const AiPerformanceCard: React.FC<{
  editSimilarity: number;
  avgResolutionMinutes: number;
  autoReplyCount: number;
}> = ({ editSimilarity, avgResolutionMinutes, autoReplyCount }) => {
  const humanEditPercent = editSimilarity > 0 ? Math.max(0, Math.round((100 - editSimilarity) * 10) / 10) : 0;

  return (
    <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            AI Draft Performance & Edit Distance
          </h3>
          <p className="text-xs text-gray-500">Human modification metrics & response resolution speed</p>
        </div>
        <span className="px-2.5 py-1 text-xs font-bold rounded-full bg-purple-100 text-purple-800 border border-purple-200">
          Analytics
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
          <span className="text-xs font-semibold text-gray-500 block">AI Draft Accuracy</span>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-2xl font-extrabold text-blue-600 tracking-tight">{editSimilarity}%</span>
            <span className="text-[11px] font-medium text-emerald-600">Unedited AI text</span>
          </div>
          <div className="w-full bg-gray-200 h-2 rounded-full mt-2 overflow-hidden">
            <div className="bg-blue-600 h-full transition-all duration-500" style={{ width: `${editSimilarity}%` }} />
          </div>
        </div>

        <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
          <span className="text-xs font-semibold text-gray-500 block">Human Edit Distance</span>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-2xl font-extrabold text-purple-600 tracking-tight">{humanEditPercent}%</span>
            <span className="text-[11px] font-medium text-gray-500">Agent edit delta</span>
          </div>
          <div className="w-full bg-gray-200 h-2 rounded-full mt-2 overflow-hidden">
            <div className="bg-purple-500 h-full transition-all duration-500" style={{ width: `${humanEditPercent}%` }} />
          </div>
        </div>

        <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
          <span className="text-xs font-semibold text-gray-500 block">Avg Resolution Time</span>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-2xl font-extrabold text-emerald-600 tracking-tight">{avgResolutionMinutes} min</span>
            <span className="text-[11px] font-medium text-gray-400">First-contact speed</span>
          </div>
          <p className="text-[11px] text-gray-500 mt-2 font-medium">{autoReplyCount} replies sent so far</p>
        </div>
      </div>
    </div>
  );
};
